/**
 * 合同编辑及签署管理共用的分享面板。显式生成才固定 PDF，并在首次生成后自动通知分包商；
 * 短信/邮件回执直接显示，失败渠道可单独重试，成功渠道不会因页面重开而重复发送。
 * CEO 固定为 Peter Li，由后端绑定账号；面板不再要求选人，也不依赖 CEO 候选列表。
 */
import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Download, ExternalLink, FileSignature, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { signingLabel } from "./SubcontractSigning";
import { fmtDT } from "../lib/date";
import { detailToText } from "../lib/detailText";

export function SubcontractSigningLinkDialog({ api, sid, zh, open, onClose, autoGenerate = false, onGenerated }) {
  return <Dialog open={open} onOpenChange={v => !v && onClose()}>
    <DialogContent className="max-w-xl max-h-[90dvh] overflow-y-auto z-[97]" overlayClassName="z-[96]">
      <DialogHeader><DialogTitle>{zh ? "合同签署链接" : "Contract signing link"}</DialogTitle>
        <DialogDescription>{zh ? "分包商与 CEO Peter Li 可任意先后签署，双方完成后自动归档。" : "The subcontractor and CEO Peter Li may sign in either order. The contract is archived after both sign."}</DialogDescription></DialogHeader>
      {open && <SigningLinkPanel key={sid} api={api} sid={sid} zh={zh} autoGenerate={autoGenerate} onGenerated={onGenerated} />}
    </DialogContent>
  </Dialog>;
}

function SigningLinkPanel({ api, sid, zh, autoGenerate, onGenerated }) {
  const T = (z, e) => zh ? z : e;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [copied, setCopied] = useState(false);
  const [results, setResults] = useState(null), [reason, setReason] = useState("");
  const [noticeCopied, setNoticeCopied] = useState(false), [downloading, setDownloading] = useState(false);
  const initialized = useRef(false), generated = useRef(onGenerated);
  generated.current = onGenerated;
  const base = `/subcontracts/${sid}/signing/link`;
  const fail = e => setError(detailToText(e?.response?.data?.detail) || e.message);
  // 签署人由服务端固定；这里只提交重试原因和续期意图，避免页面继续传递可选账号。
  const create = async (renew = false) => {
    setBusy(true); setError(""); setResults(null);
    try {
      const r = await api.post(base, { reason, renew }); setData(r.data); generated.current?.();
    } catch (e) {
      // 生成失败时服务器可能已留下 failed 版本，读回后允许填写原因重试，不能卡在旧状态。
      try { setData((await api.get(base)).data); } catch { /* 保留原错误以便重试 */ }
      fail(e);
    }
    finally { setBusy(false); }
  };
  useEffect(() => {
    // 用户刚点过“保存并生成”时直接执行；固定 Peter Li 后不再等待选人。
    // 一般打开管理面板仍只读，失败或撤回的版本必须先填原因再重新发起。
    if (initialized.current) return;
    initialized.current = true;
    setBusy(true);
    api.get(base).then(async (link) => {
      setData(link.data);
      if (autoGenerate && !link.data.url && !["failed", "withdrawn"].includes(link.data.status)) await create();
    }).catch(fail).finally(() => setBusy(false));
  }, [api, base, autoGenerate]); // eslint-disable-line react-hooks/exhaustive-deps
  const url = data?.url ? new URL(data.url, window.location.origin).href : "";
  // 需求（2026-10-24）：公司要求全局短信/邮件总开关保持关闭，签署通知经常要由工作人员手动
  // 转发（微信/短信）。除复制裸链接外，提供「复制通知内容」（后端下发的与自动外发一致的
  // 英文文案 + 链接）和「下载合同 PDF」（鉴权 blob 下载，直接当附件发给分包商）。
  const copyNotice = async () => {
    try {
      await navigator.clipboard.writeText(data.message || url);
      setNoticeCopied(true);
    } catch { setError(T("复制失败，请选中上方链接手动复制", "Copy failed. Select the link above to copy manually.")); }
  };
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get(`/subcontracts/${sid}/signing/file/preview`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      a.download = `subcontract-${data?.tradeName || sid}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch (e) { setError(detailToText(e?.response?.data?.detail) || e.message); }
    finally { setDownloading(false); }
  };
  const send = async () => {
    const failedChannels = ["sms", "email"].filter(c => data?.delivery?.[c] !== true);
    if (!failedChannels.length) return;
    setBusy(true); setError(""); setResults(null);
    try {
      const r = await api.post(`${base}/send`, { channels: failedChannels });
      setResults(r.data.results);
      setData(current => ({ ...current, delivery: { ...(current?.delivery || {}), ...r.data.results } }));
    }
    catch (e) { fail(e); } finally { setBusy(false); }
  };
  return <div className="space-y-4" data-testid="subcontract-signing-link">
    {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700 break-words">{error}</p>}
    {!data && busy && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />{T("正在准备签署…", "Preparing signing…")}</p>}
    {data && <p className="text-sm font-medium text-brand-600" role="status">{signingLabel(data.status, zh, data)}</p>}
    {url ? <>
      <div className="flex flex-col sm:flex-row gap-4 rounded-xl border border-slate-200 p-4">
        <div className="shrink-0 self-center bg-white p-2 rounded-lg"><QRCodeSVG value={url} size={144} title={T("合同签署二维码", "Contract signing QR code")} /></div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-semibold text-sm">{T("手机扫码签署", "Scan to sign on your phone")}</p>
          <p className="text-xs text-slate-500">{T("无需登录或短信验证码。请仅分享给该合同的分包商。", "No login or SMS code. Share only with this subcontractor.")}</p>
          <label className="block text-xs text-slate-500">{T("专属签署链接", "Private signing link")}<Input value={url} readOnly onFocus={e => e.target.select()} className="mt-1 min-h-[44px] text-sm" /></label>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="min-h-[44px]" onClick={async () => { try { await navigator.clipboard.writeText(url); setCopied(true); } catch { setError(T("复制失败，请选中上方链接手动复制", "Copy failed. Select the link above to copy manually.")); } }}><Copy className="h-4 w-4 mr-2" />{copied ? T("已复制", "Copied") : T("复制链接", "Copy link")}</Button>
            <Button variant="outline" className="min-h-[44px]" onClick={copyNotice}><Copy className="h-4 w-4 mr-2" />{noticeCopied ? T("已复制通知", "Copied") : T("复制通知内容", "Copy notice")}</Button>
            <Button variant="outline" className="min-h-[44px]" disabled={downloading} onClick={downloadPdf}>{downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}{T("下载合同 PDF", "Download PDF")}</Button>
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 min-h-[44px] px-2 text-sm text-brand-600"><ExternalLink className="h-4 w-4" />{T("打开", "Open")}</a>
          </div>
          <p className="text-xs text-slate-500">{T("总开关关闭期间短信/邮件不会自动发出：可复制通知内容或下载 PDF，由工作人员微信/短信手动发送给分包商。", "While the messaging master switch is off, SMS/email are not sent automatically. Copy the notice or download the PDF and forward them to the subcontractor manually.")}</p>
          <p className="text-xs text-slate-500">{T("有效期至 ", "Expires ")}{fmtDT(data.expiresAt)}</p>
        </div>
      </div>
      {data.status === "sub_pending" && <div className="space-y-2" data-testid="subcontract-signing-delivery">
        <p className="text-xs text-slate-500">{T("生成链接时会自动通知分包商账号，并尝试发送短信和邮件；请检查各渠道状态。", "The subcontractor account is notified and SMS/email are attempted automatically when the link is created.")}</p>
        {["portal", "sms", "email"].map(c => {
          const ok = results?.[c] ?? data.delivery?.[c];
          // 需求（2026-10-24，老板）：公司要求全局短信/邮件总开关保持关闭，未发出的渠道必须
          // 说明原因（开关关闭 / 凭证失效 / 未绑定账号），由后端随回执返回，直接展示并引导联系管理员。
          const why = results?.[`${c}Info`] ?? data.delivery?.[`${c}Info`];
          return <div key={c}>
            <p className={`text-sm ${ok ? "text-emerald-700" : "text-amber-700"}`}>
              {c === "portal" ? T("分包商系统通知", "Subcontractor portal") : c === "sms" ? T("短信", "SMS") : T("邮件", "Email")} · {ok ? T("已发送", "Sent") : T("未发送", "Not sent")}
            </p>
            {!ok && why && <p role="alert" className="pl-3 text-xs text-amber-700 break-words">{why}</p>}
          </div>;
        })}
        {["sms", "email"].some(c => (results?.[c] ?? data.delivery?.[c]) !== true) && <Button variant="outline" className="min-h-[44px]" disabled={busy} onClick={send}>{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}{T("重试未成功渠道", "Retry failed channels")}</Button>}
      </div>}
    </> : data && !["preparing", "ceo_pending", "completed"].includes(data.status) && <div className="space-y-3">
      {["failed", "withdrawn"].includes(data.status) && <label className="block text-sm">{T("重新发起原因", "Retry reason")}<Input value={reason} onChange={e => setReason(e.target.value)} className="mt-1 min-h-[44px]" /></label>}
      <p className="text-xs text-slate-500">{T("生成后固定当前 PDF；修改合同需先撤回签署。", "Creating the link locks the current PDF. Withdraw signing before editing.")}</p>
      <Button disabled={busy || (["failed", "withdrawn"].includes(data.status) && !reason.trim())} onClick={() => create()} className="w-full min-h-[44px] bg-brand-500 hover:bg-brand-600">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSignature className="h-4 w-4 mr-2" />}{T(data.status === "sub_pending" ? "重新生成有效链接" : "固定版本并生成签署链接", data.status === "sub_pending" ? "Generate a valid link" : "Freeze version & create signing link")}</Button>
    </div>}
    <a href={`/subcontracts/${sid}/signing`} className="text-sm text-brand-600 inline-flex items-center gap-2 min-h-[44px]">{T("签署进度与撤回管理", "Signing progress & withdrawal")}<ExternalLink className="h-4 w-4" /></a>
  </div>;
}
